import { Typography, Button } from '@frontend/design_system'
import { PlusIcon } from 'ui/icons'
import { useState } from 'react'
import { DocumentsControlModal } from './components/DocumentsControlModal'
import { StatusBadge } from 'ui/atoms/StatusBadge'
import { statusBadge } from 'modules/PendingForApproval/components/PendingCard/constants'
import { StatusBadgeProps } from 'ui/atoms/StatusBadge/types'
import { format } from 'date-fns'
import { DOCUMENTS_UPDATE_FORMAT } from 'constants/date'
import { STATUS_BADGE_STATUSES } from 'texts/pendingForApproval'
import { isOperatorSelector } from 'redux/login/selectors'
import { useAppSelector } from 'redux/hooks'
import { FileList } from './components/FileList/FileList'
import { useCompanyId } from 'hooks/useCompanyId'
import { OperatorActionButtons } from '../OperatorActionButtons'
import { REGISTRATION_STATUSES } from 'api/auth/constants'
import { DOCUMENT_LIST } from 'texts/agreements'
import {
  DocumentsControlProps,
  DocumentsModalVariant,
  ModalVariant,
} from './types'
import { ADD } from 'texts/common'
import { ACTUAL, ARCHIVED, LAST_UPDATED } from 'texts/documents'
import styles from './styles.module.scss'

export const DocumentsControls = ({
  documentType,
  documents,
  onReload,
}: DocumentsControlProps) => {
  const [modalVariant, setModalVariant] = useState<ModalVariant>(null)

  const isOperator = useAppSelector(isOperatorSelector)
  const companyId = useCompanyId()

  const isActualDocumentsExist = documents.actual.files.length > 0

  const handleAddModalOpen = () => {
    setModalVariant(DocumentsModalVariant.Add)
  }

  const handleReplaceModalOpen = () => {
    setModalVariant(DocumentsModalVariant.Replace)
  }

  const handleCloseModal = () => {
    setModalVariant(null)
  }

  const isActualDocumentsInReview =
    documents.actual.status === REGISTRATION_STATUSES.REVIEW &&
    documents.actual.files.length > 0

  const renderFileList = () => {
    if (isOperator) {
      return (
        <div className={styles['documents-control-list']}>
          <Typography name="Subtitle2" color="grey400">
            {ACTUAL}
          </Typography>
          <FileList
            documents={documents.actual.files}
            date={documents.actual.date}
            status={documents.actual.status}
            documentType={documentType}
            handleReplaceModalOpen={handleReplaceModalOpen}
          />
          {documents.archieved.length > 0 && (
            <>
              <Typography name="Subtitle2" color="grey400">
                {ARCHIVED}
              </Typography>
              {documents.archieved.map((group) => (
                <FileList
                  key={group.date}
                  isArchived
                  documents={group.files}
                  date={group.date}
                  status={group.status}
                  handleReplaceModalOpen={handleAddModalOpen}
                  documentType={documentType}
                />
              ))}
            </>
          )}
        </div>
      )
    } else {
      return (
        <FileList
          documents={documents.actual.files}
          date={documents.actual.date}
          status={documents.actual.status}
          handleReplaceModalOpen={handleAddModalOpen}
          documentType={documentType}
        />
      )
    }
  }

  return (
    <>
      {modalVariant && (
        <DocumentsControlModal
          onModalClose={handleCloseModal}
          id={documentType}
          onReload={onReload}
          variant={
            modalVariant === DocumentsModalVariant.Replace
              ? DocumentsModalVariant.Replace
              : undefined
          }
        />
      )}
      <div className={styles['documents-control']}>
        <div className={styles['documents-control-header']}>
          <Typography
            name="subtitleWBold"
            className={styles['documents-control-header-title']}
          >
            {DOCUMENT_LIST[documentType]}
            {isActualDocumentsExist && !isOperator && (
              <StatusBadge
                label={STATUS_BADGE_STATUSES[documents.actual.status]}
                status={
                  statusBadge[
                    documents.actual.status
                  ] as StatusBadgeProps['status']
                }
              />
            )}
          </Typography>
          {!isActualDocumentsExist && (
            <Button
              label={ADD}
              onClick={handleAddModalOpen}
              typographyName="Button2"
              widthClassName={styles['button-wrapper']}
              size="small"
              iconRight={<PlusIcon color="whiteMaster" />}
            />
          )}
          {documents.actual.date && !isOperator && (
            <Typography name="body2WMedium" color="grey800">
              {LAST_UPDATED}{' '}
              {format(new Date(documents.actual.date), DOCUMENTS_UPDATE_FORMAT)}
            </Typography>
          )}
          {isOperator && isActualDocumentsInReview && (
            <OperatorActionButtons
              companyId={companyId.toString()}
              section="Documents"
              documentType={documentType}
              onSuccess={onReload}
            />
          )}
        </div>
        {isActualDocumentsExist && renderFileList()}
      </div>
    </>
  )
}
